import {useCallback, useEffect, useState} from 'react';

import {API_URL} from 'constant';
import {fmtDate} from 'views/design-system/format';
import {useNetwork, useNotification} from 'hooks';

export interface IEvaultAddonStatus {
  active: boolean;
  activatedAt: number | null;
  /** ms epoch of a pending switch-off; null while simply on or off. */
  endsAt: number | null;
  available: boolean;
  /** ms epoch the open billing cycle ends; null with no subscription. The one date both the billing page and the eVault drive quote for "stays on until…". */
  cycleEnd: number | null;
  reason?: 'no-subscription';
  price: number;
  transferPrice: number;
  /** What this cycle's invoice carries for the add-on. */
  monthlyCharge: number;
  usage: {quantity: number; amount: number};
}

/**
 * Billing-page state for the eVault add-on toggle (spec 2026-09-09).
 * `purchase` switches it on (accrues to the monthly invoice, no charge now);
 * `cancel` switches it off at the end of the cycle. Amounts come from the server.
 */
export const useEvaultAddon = () => {
	const [status, setStatus] = useState<IEvaultAddonStatus | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);
	const [isPurchasing, setIsPurchasing] = useState(false);

	const {get, post, remove} = useNetwork({updateState: false});
	const {successNotification, errorNotification} = useNotification();

	const fetchStatus = useCallback(async () => {
		const resp = await get(API_URL.EVAULT_ADDONS);
		if (resp?.message?.toLowerCase() === 'ok') {
			setStatus(resp?.data?.[0] ?? null);
		}
		setIsLoaded(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* `post()`/`remove()` in useNetwork are NOT `get()`: they already unwrap the
	   `{message, data}` envelope and hand back `data` itself on success — so a
	   live row arrives as `resp.active`, never `resp.data.active`. Both return
	   `null` on failure, but NOT for the same reason: `post()`'s axios catch has
	   already toasted a real 4xx's own message (so a null response there means
	   "already told the user" — toasting again would double it), while
	   `remove()` goes through `fetch`, which does not throw on a 4xx at all, so
	   it never toasts one — the fallback is the ONLY message a switch-off
	   failure ever gets. (Verified against `hooks/network/network.ts`.) */
	const purchase = useCallback(async (): Promise<boolean> => {
		setIsPurchasing(true);
		const resp = await post(API_URL.EVAULT_ADDONS, {});
		setIsPurchasing(false);
		if (resp?.active) {
			setStatus(resp);
			successNotification('eVault is on. $' + Number(resp.price).toFixed(2) + ' will be on this month\'s invoice.');
			return true;
		}
		// A null resp means post() already toasted the server's own reason;
		// only a response that arrived without `active: true` is unexplained.
		if (resp) errorNotification('Could not add eVault to your plan. Please try again.');
		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorNotification, successNotification]);

	const cancel = useCallback(async (): Promise<boolean> => {
		setIsPurchasing(true);
		const resp = await remove(`${API_URL.EVAULT_ADDONS}/current`);
		setIsPurchasing(false);
		if (resp && typeof resp.active === 'boolean') {
			setStatus(resp);
			/* The SAME formatter the two off-dialogs use. A toast reading
			   "until 30/09/2026" beside a dialog that said "until Sep 30, 2026"
			   is two answers to one question. */
			const ends = resp.endsAt ? fmtDate(resp.endsAt) : 'the end of this cycle';
			successNotification(`eVault stays on until ${ends}, then switches off.`);
			return true;
		}
		// remove() never toasts on its own (see comment above) — every failure,
		// explained or not, needs this fallback.
		errorNotification('Could not switch eVault off. Please try again.');
		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorNotification, successNotification]);

	useEffect(() => {
		void fetchStatus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {status, isLoaded, isPurchasing, purchase, cancel, refetch: fetchStatus};
};
